import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Popover,
  Typography,
  Button,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import { CalendarMonth, Close } from "@mui/icons-material";
import { CustomDatePicker } from "../../../components/formsComponents/CustomDatePicker";
import { ControlledCustomSelect } from "../../../components/formsComponents";
import { HIGHLIGHT_COLORS } from "../../../constants/appColors";

const monthList = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const monthShortNames: Record<string, string> = {
  "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr",
  "05": "May", "06": "Jun", "07": "Jul", "08": "Aug",
  "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec",
};

const yearList = [
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map((x) => ({
    label: `202${x}`,
    value: `202${x}`,
  })),
  { label: "2030", value: "2030" },
];

export interface IFunctionDateState {
  functionDate: string | null;
  functionMonth: string | null;
  functionYear: string | null;
}

interface IFunctionDateFilterProps {
  value: IFunctionDateState;
  onChange: (state: IFunctionDateState) => void;
  sx?: object;
}

const EMPTY_STATE: IFunctionDateState = {
  functionDate: null,
  functionMonth: null,
  functionYear: null,
};

export const FunctionDateFilter: React.FC<IFunctionDateFilterProps> = ({
  value,
  onChange,
  sx,
}) => {
  // Guard against stale cached value where functionDate may be an object {month, year}
  const safeValue: IFunctionDateState = {
    functionDate: typeof value.functionDate === "string" ? value.functionDate : null,
    functionMonth: typeof value.functionMonth === "string" ? value.functionMonth : null,
    functionYear: typeof value.functionYear === "string" ? value.functionYear : null,
  };
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [local, setLocal] = useState<IFunctionDateState>(safeValue);

  const open = Boolean(anchorEl);
  const hasActiveFilter = !!(safeValue.functionDate || safeValue.functionMonth || safeValue.functionYear);

  // Sync local when parent value changes
  useEffect(() => {
    setLocal(safeValue);
  }, [safeValue.functionDate, safeValue.functionMonth, safeValue.functionYear]);

  const getDisplayLabel = (): string => {
    if (safeValue.functionDate) return safeValue.functionDate;
    if (safeValue.functionMonth && safeValue.functionYear) {
      return `${monthShortNames[safeValue.functionMonth] || safeValue.functionMonth} ${safeValue.functionYear}`;
    }
    if (safeValue.functionMonth) return monthShortNames[safeValue.functionMonth] || safeValue.functionMonth;
    if (safeValue.functionYear) return safeValue.functionYear;
    return "Event Date";
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setLocal(safeValue);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (local.functionDate) {
      onChange({ functionDate: local.functionDate, functionMonth: null, functionYear: null });
    } else {
      onChange({ functionDate: null, functionMonth: local.functionMonth, functionYear: local.functionYear });
    }
    handleClose();
  };

  const handleClear = () => {
    setLocal(EMPTY_STATE);
    onChange(EMPTY_STATE);
    handleClose();
  };

  return (
    <>
      <Box
        position="relative"
        sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: 1,
          px: 1,
          height: 36,
          cursor: "pointer",
          minWidth: 140,
          maxWidth: 220,
          ...(hasActiveFilter ? {
            backgroundColor: (theme) => HIGHLIGHT_COLORS.background[theme.palette.mode],
            border: "2px solid",
            borderColor: (theme) => HIGHLIGHT_COLORS.border[theme.palette.mode],

            "&:hover": {
              borderColor: (theme) => HIGHLIGHT_COLORS.borderActive[theme.palette.mode]
            },
          } : {
            border: "1px solid",
            borderColor: "divider",
            "&:hover": { borderColor: "#5d5d5dff" },
          }),

          ...sx,
        }}
        onClick={handleOpen}
      >
        {hasActiveFilter && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: -9,
              left: 8,
              px: 0.5,
              // When active, the field fills amber, so the label notch must
              // match that fill (not white) to stay consistent with the other
              // highlighted filters.
              backgroundColor: "#fff2c7",
              color: "#5a5a5a",
              // Match the standard CustomDatePicker shrink-label (Follow-up
              // Date): 0.8rem / 700.
              fontWeight: 700,
              lineHeight: 1,
              pointerEvents: "none",
              fontSize: "0.8rem",
            }}
          >
            Event Date
          </Typography>
        )}
        <Typography
          variant="body2"
          noWrap
          sx={{
            flex: 1,
            // Match the standard picker input text color.
            color: hasActiveFilter ? "#454545" : "text.secondary",
            fontSize: "0.8rem",
            fontWeight: 400,
          }}
        >
          {getDisplayLabel()}
        </Typography>
        {hasActiveFilter && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            sx={{ p: 0.25, ml: 0.5 }}
          >
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <CalendarMonth
          fontSize="small"
          sx={{ color: "action.active", ml: 0.5 }}
        />
      </Box >

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { p: 2, width: 300, mt: 0.5 } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Filter by Event Date
        </Typography>

        {/* Exact Date */}
        <CustomDatePicker
          placeholder="Event Date"
          value={local.functionDate}
          onChange={(newValue) => {
            setLocal({
              functionDate: newValue,
              functionMonth: null,
              functionYear: null,
            });
          }}
          fullWidth
        />

        <Divider sx={{ my: 1.5 }}>
          <Chip label="OR" size="small" />
        </Divider>

        {/* Month / Year */}
        <Stack direction="row" spacing={1}>
          <ControlledCustomSelect
            fullWidth
            name="month"
            label="Event Month"
            value={local.functionMonth || ""}
            onChange={(e: any) => {
              setLocal({
                functionDate: null,
                functionMonth: e.target.value as string,
                functionYear: local.functionYear,
              });
            }}
            displayEmpty
            options={monthList}
          />
          <ControlledCustomSelect
            fullWidth
            name="year"
            label="Event Year"
            value={local.functionYear || ""}
            onChange={(e: any) => {
              setLocal({
                functionDate: null,
                functionMonth: local.functionMonth,
                functionYear: e.target.value as string,
              });
            }}
            displayEmpty
            options={yearList}
          />
        </Stack>

        {/* Actions */}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
          <Button size="small" onClick={handleClear} color="inherit">
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disabled={!local.functionDate && !local.functionMonth && !local.functionYear}
          >
            Apply
          </Button>
        </Stack>
      </Popover>
    </>
  );
};
